import { useMemo, useRef } from "react";
import Slider from "react-slick";
import { useDesktopSlides } from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./HolidayPackageSection.module.css";
import { FaStar, FaLocationDot } from "react-icons/fa6";
import { LuWallet } from "react-icons/lu";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import PackageCountdown from "@/Component/Common/PackageCountdown";
import { getReviewCount, getSeatsLeftLabel } from "@/utils/packageCardMeta";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { isIndianUser } from "@/utils/getCountryCode";
import { withRegionPath } from "@/utils/session";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { map } from "lodash";
import { INCLUSION_ICONS } from "@/utils/Inclusions";

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const handleRouteRegion = (path) => {
  return isIndianUser() ? `/in${path}` : path;
};

function getSectionList(data, moduleType) {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  const listKey = moduleType === "activity" ? "activityList" : "holidayList";
  if (Array.isArray(data[listKey])) return data[listKey];
  if (Array.isArray(data.data?.[listKey])) return data.data[listKey];

  return [];
}

function PackageCard({ item, moduleType = "holiday" }) {
  const isActivity = moduleType === "activity";
  const title = isActivity
    ? item.activityName || item.packageName || ""
    : item.packageName || item.title || "";
  const imagePath = item.imagePath || item.image || "";
  const rating = item.rating;
  const reviewCount = getReviewCount(item);
  const seatsLeft = getSeatsLeftLabel(item);
  const discountTag = isActivity ? item.tag2 || item.discountTag : item.discountTag;
  const nights = Number(item.nights) || 0;
  const location = isActivity
    ? `${item.cityName}${item.countryName ? `, ${item.countryName}` : ""}`
    : "";
  const citiesLine = isActivity
    ? ""
    : map(item.cities, (city) => {
      const cityName =
        city?.city?.cityName ||
        (typeof city?.city === "string" ? city.city : "") ||
        city?.cityName ||
        "";
      return city?.nights != null && cityName
        ? `${city.nights}N ${cityName}`
        : cityName;
    })
      .filter(Boolean)
      .join(" · ");
  const inclusions = {
    flight: Boolean(item.isFlightIncluded),
    meal: Boolean(item.isMealsIncluded),
    transfer: Boolean(item.isTransferIncluded),
    hotel: Boolean(item.isHotelIncluded),
    visa: Boolean(item.isVisaIncluded),
    activity: Boolean(item.isActivityIncluded),
  };
  const activeInclusions = INCLUSION_ICONS.filter(({ id }) => inclusions?.[id]);
  const detailHref = isActivity
    ? handleRouteRegion(`/activity/${item.slug}`)
    : handleRouteRegion(`/holiday/${item.slug}`);

  return (
    <article className={styles.card}>
      <a href={detailHref} className={styles.imageLink} aria-label={title}>
        <div className={styles.imageWrap}>
          <img src={imagePath} alt={title} className={styles.cardImage} />

          {item.tagOnThumbnail ? (
            <span className={styles.badgeBest}>
              <FaStar style={{ marginTop: "-2px" }} />
              {item.tagOnThumbnail}
            </span>
          ) : null}
          {seatsLeft ? <span className={styles.badgeSeats}>{seatsLeft}</span> : null}
          {!isActivity && item.badge?.type === "emi" ? (
            <span className={styles.badgeEmi}>
              <LuWallet style={{ marginTop: "-2px" }} />
              {item.badge.label} &nbsp;·&nbsp; {item.badge.month}
            </span>
          ) : null}

          {!isActivity && nights > 0 ? (
            <span className={styles.duration}>
              {nights}N / {nights + 1}D
            </span>
          ) : null}
          {rating ? (
            <span className={styles.rating}>
              <strong style={{ color: "#0B192C", marginTop: "2px" }}>{rating}</strong>
              <FaStar color="#FFB835" />
              {reviewCount != null ? ` (${reviewCount})` : null}
            </span>
          ) : null}
        </div>
      </a>

      <div className={styles.body}>
        <h3 className={styles.cardTitle}>
          <a href={detailHref} className={styles.titleLink}>{title}</a>
        </h3>
        {isActivity ? (
          location ? (
            <p className={styles.locationLine}>
              <FaLocationDot size={12} />
              <span>{location}</span>
            </p>
          ) : null
        ) : (
          <p className={styles.cardSubtitle}>{citiesLine}</p>
        )}
        <div className={styles.cardIncludes}>
          <div className={styles.inclusion}>
            {isActivity
              ? map(item?.includes, (feature, index) => (
                <span
                  key={`${feature.includesTypeName || "inc"}-${index}`}
                  className={styles.includeIcon}
                  title={feature.includesTypeName || ""}
                >
                  {feature.includesImagePath ? (
                    <img src={feature.includesImagePath} alt={feature.includesTypeName || ""} />
                  ) : null}
                </span>
              ))
              : map(activeInclusions, (inclusion) => (
                <span key={inclusion.id} className={styles.includeIcon}>
                  {(inclusion.id === "flight" && item.flightSubText) ?
                    <span className={styles.flightSubTextTip}>{item.flightSubText}</span> :
                    null
                  }
                  {inclusion.icon}
                </span>
              ))}
          </div>
          {!isActivity ? (
            <PackageCountdown item={item} className={styles.cardTimer} />
          ) : null}
        </div>
        <div className={styles.footer}>
          {discountTag ? <span className={styles.discountTag}>{discountTag}</span> : null}
          <div className={styles.footerContent}>
            <div className={styles.priceBlock}>
              <p className={styles.starting}>
                Starting From
                {item.displayCutPrice ? (
                  <span className={styles.oldPrice}>
                    {getCurrencySymbol("sub", 10)}
                    {formatIndianPrice(item.displayCutPrice)}
                  </span>
                ) : null}
              </p>
              <p className={styles.price}>
                {getCurrencySymbol("main", 16)}
                {formatIndianPrice(item.price)}
                <span className={styles.perPerson}>/Person</span>
              </p>
            </div>
            <a href={detailHref} className={styles.viewDealsBtn}>
              View Deal
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function HolidayPackageSection({
  title,
  subtitle,
  data,
  viewAllHref,
  className = "",
  hideWhenEmpty = false,
  moduleType = "holiday",
}) {
  const { isDesktop, mounted, slidesToShow } = useDesktopSlides();
  const sliderRef = useRef(null);
  const list = useMemo(() => getSectionList(data, moduleType), [data, moduleType]);

  const sectionTitle =
    title ||
    data?.title ||
    (moduleType === "activity" ? "Featured Activities" : "Featured Journeys");
  const sectionSubtitle =
    subtitle ||
    data?.subTitle ||
    data?.subtitle ||
    (moduleType === "activity"
      ? "Explore the experiences you dream of"
      : "Holidays our experts actually took");
  const defaultViewAll =
    moduleType === "activity"
      ? withRegionPath("/activity/list")
      : withRegionPath("/holiday/list");
  const viewAllLink = viewAllHref || data?.viewAllUrl || defaultViewAll;

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    list.length,
    slidesToShow,
    moduleType
  );

  const slickSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = list.length <= slidesToShow;
  const hasItems = list.length > 0;
  const showEmpty = !hasItems && (hideWhenEmpty || data != null);

  return (
    <section className={`${styles.section} standard-padding ${className}`.trim()}>
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className="standard-heading">{sectionTitle}</h2>
          {sectionSubtitle ? (
            <p className="standard-subheading">{sectionSubtitle}</p>
          ) : null}
        </div>

        {hasItems ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Previous"
              disabled={!canPrev}
              onClick={() => {
                if (typeof sliderRef.current?.slickPrev === "function") {
                  sliderRef.current.slickPrev();
                }
              }}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Next"
              disabled={!canNext}
              onClick={() => {
                if (typeof sliderRef.current?.slickNext === "function") {
                  sliderRef.current.slickNext();
                }
              }}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}

        {hasItems ? (
          <a href={viewAllLink} className={styles.viewAll}>
            View All
            <ArrowIcon />
          </a>
        ) : null}
      </div>

      {showEmpty || (hideWhenEmpty && !hasItems) ? (
        <p className="nodata">No data found</p>
      ) : hasItems && showSlick ? (
        <div className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}>
          <Slider ref={sliderRef} {...slickSettings}>
            {list.map((item) => (
              <div
                key={item.activityId || item.packageId || item.slug || item.id}
                className={styles.slickSlide}
              >
                <PackageCard item={item} moduleType={moduleType} />
              </div>
            ))}
          </Slider>
        </div>
      ) : hasItems ? (
        <div className={styles.slider}>
          {list.map((item) => (
            <PackageCard
              key={item.activityId || item.packageId || item.slug || item.id}
              item={item}
              moduleType={moduleType}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
